import { Box, InputBase, Modal, Typography, useTheme } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';

import { AddFilterCategories } from './AddFilterCategories';
import { AddFilterConfigure } from './AddFilterConfigure';
import { AddFilterFieldList } from './AddFilterFieldList';
import {
  FilterFieldDef,
  FILTER_FIELD_DEFINITIONS,
  FILTER_CATEGORIES,
} from './AddFilterModal.types';
import { AdvancedFilter } from './FilterBar.props';

export interface AddFilterModalProps {
  open: boolean;
  onClose: () => void;
  onApply: (filter: AdvancedFilter) => void;
  recentlyUsed: string[];
  /** Override the field catalog. Defaults to the service-list catalog. */
  fieldDefinitions?: FilterFieldDef[];
  /** Override the category tabs. Defaults to the service-list categories. */
  categories?: string[];
  /** Option source map for `type: 'list'` fields, keyed by FilterFieldDef.key.
   *  Keys without an entry render as a comma-separated text input. */
  listOptions?: Record<string, Array<{ label: string; value: string }>>;
  /** When supplied, the modal opens directly to the configure step pre-populated
   *  with this filter's values. Used for click-to-edit on active chips. */
  editingFilter?: AdvancedFilter;
}

export const AddFilterModal: React.FC<AddFilterModalProps> = ({
  open,
  onClose,
  onApply,
  recentlyUsed,
  fieldDefinitions,
  categories: categoriesProp,
  listOptions,
  editingFilter,
}) => {
  const fields = fieldDefinitions ?? FILTER_FIELD_DEFINITIONS;
  const categoryNames = categoriesProp ?? FILTER_CATEGORIES;
  const theme = useTheme();
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedField, setSelectedField] = useState<FilterFieldDef | null>(null);

  const isEditing = Boolean(editingFilter);

  // When opening in edit mode, jump straight to the configure step with the
  // filter's field pre-selected. When closing, reset so the next open starts
  // fresh on the field list.
  useEffect(() => {
    if (!open) {
      setSelectedField(null);
      setSearchQuery('');
      return;
    }
    if (editingFilter) {
      const match = fields.find((f) => f.key === editingFilter.dataKey) ?? null;
      setSelectedField(match);
    }
  }, [open, editingFilter, fields]);

  const categories = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const matches = q ? fields.filter((f) => f.name.toLowerCase().includes(q)) : fields;
    const counts: Record<string, number> = {};
    matches.forEach((f) => {
      counts[f.category] = (counts[f.category] || 0) + 1;
    });
    // Drop categories with no fields in the (possibly restricted, possibly
    // search-filtered) catalog. Keeps the client-view sidebar tight — the
    // curated report only exposes a subset of columns, so most categories
    // would otherwise show "(0)" with nothing inside.
    return categoryNames
      .map((name) => ({
        name,
        count: name === 'All' ? matches.length : counts[name] || 0,
      }))
      .filter((c) => c.count > 0);
  }, [searchQuery, fields, categoryNames]);

  // If a search (or a restricted field catalog) removes the active category,
  // fall back to 'All' so the field list still has somewhere to render from.
  const effectiveActiveCategory = useMemo(
    () => (categories.some((c) => c.name === activeCategory) ? activeCategory : 'All'),
    [categories, activeCategory]
  );

  const handleFieldSelect = (field: FilterFieldDef) => {
    setSelectedField(field);
  };

  const handleBack = () => {
    setSelectedField(null);
  };

  const handleApply = (filter: AdvancedFilter) => {
    onApply(filter);
    setSelectedField(null);
    setSearchQuery('');
    onClose();
  };

  const handleClose = () => {
    setSelectedField(null);
    setSearchQuery('');
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} aria-labelledby="add-filter-modal-title">
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 780,
          height: 620,
          bgcolor: theme.palette.v4.surface,
          borderRadius: 2,
          boxShadow: 24,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ px: 2, pt: 2, pb: 1.5, borderBottom: `1px solid ${theme.palette.v4.border}` }}>
          <Typography id="add-filter-modal-title" variant="subtitle1" sx={{ fontWeight: 600 }}>
            Add Filter
          </Typography>
          {!selectedField && (
            <InputBase
              placeholder="Search fields..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              fullWidth
              inputProps={{ 'aria-label': 'Search filter fields' }}
              sx={{
                mt: 1,
                border: `1px solid ${theme.palette.v4.filterInputBorder}`,
                borderRadius: 1,
                px: 1.5,
                py: 0.5,
                fontSize: '0.875rem',
              }}
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {selectedField ? (
            <AddFilterConfigure
              field={selectedField}
              onApply={handleApply}
              onBack={isEditing ? handleClose : handleBack}
              options={listOptions?.[selectedField.key]}
              initialFilter={editingFilter}
            />
          ) : (
            <>
              <AddFilterCategories
                categories={categories}
                active={effectiveActiveCategory}
                onSelect={setActiveCategory}
              />
              <AddFilterFieldList
                fields={fields}
                category={effectiveActiveCategory}
                recentlyUsed={recentlyUsed}
                searchQuery={searchQuery}
                onSelect={handleFieldSelect}
              />
            </>
          )}
        </Box>
      </Box>
    </Modal>
  );
};
